import { capturerPortee, exigerActuelle, PorteeObsolete } from '../auth/identite.js';
import type { QueryClient } from '@tanstack/react-query';
import type { RessourceHorsLigne } from '@artosera/shared';
import { artistesApi, cleListeArtistes, type FiltresArtistes } from '../api/artistes.js';
import { appelDeRequete } from '../api/cles.js';
import {
  cleListeExpositions,
  expositionsApi,
  type FiltresExpositions,
} from '../api/expositions.js';
import {
  cleFicheOeuvre,
  cleListeOeuvres,
  oeuvresApi,
  type FiltresOeuvres,
} from '../api/oeuvres.js';

/**
 * Les filtres que les pages posent à leur ouverture (clé de cache identique,
 * donc liste retrouvée hors ligne). Si une page change ses filtres par défaut,
 * ce tableau change avec — le test le verrouille.
 *   OeuvresPage.tsx:65-79   ArtistesPage.tsx:29, :33   OeuvresPage.tsx:117   ExpositionsPage.tsx:63-71
 */
export const FILTRES_PREPARES = {
  oeuvres: [
    { vignettes: true, inclureArchivees: false, tri: 'recent', page: 1, parPage: 50 },
    { parPage: 200, vignettes: true },
  ] as FiltresOeuvres[],
  artistes: [
    { tri: 'nom', portraits: true },
    { tri: 'nom', parPage: 200 },
  ] as FiltresArtistes[],
  expositions: [{ inclureArchivees: false, parPage: 200 }] as FiltresExpositions[],
};

export const FICHES_MAX = 300;
const PARALLELE = 4;

export interface ProgressionPreparation {
  readonly faites: number;
  readonly total: number;
}

export async function preparerAppareil(
  client: QueryClient,
  slug: string,
  perimetre: readonly RessourceHorsLigne[],
  surProgression: (progression: ProgressionPreparation) => void = () => undefined,
): Promise<ProgressionPreparation> {
  const portee = capturerPortee();
  if (portee.galerieSlug !== slug) throw new PorteeObsolete();
  const listes: (() => Promise<unknown>)[] = [];
  if (perimetre.includes('oeuvres')) {
    for (const filtres of FILTRES_PREPARES.oeuvres) {
      listes.push(() =>
        client.fetchQuery({
          queryKey: cleListeOeuvres(slug, filtres),
          queryFn: (contexte) => oeuvresApi.lister(slug, filtres, appelDeRequete(contexte)),
        }),
      );
    }
  }
  if (perimetre.includes('artistes')) {
    for (const filtres of FILTRES_PREPARES.artistes) {
      listes.push(() =>
        client.fetchQuery({
          queryKey: cleListeArtistes(slug, filtres),
          queryFn: (contexte) => artistesApi.lister(slug, filtres, appelDeRequete(contexte)),
        }),
      );
    }
  }
  if (perimetre.includes('expositions')) {
    for (const filtres of FILTRES_PREPARES.expositions) {
      listes.push(() =>
        client.fetchQuery({
          queryKey: cleListeExpositions(slug, filtres),
          queryFn: (contexte) => expositionsApi.lister(slug, filtres, appelDeRequete(contexte)),
        }),
      );
    }
  }

  let faites = 0;
  let total = listes.length;
  surProgression({ faites, total });
  const resultats = [];
  for (const charger of listes) {
    exigerActuelle(portee);
    resultats.push(await charger());
    exigerActuelle(portee);
    faites += 1;
    surProgression({ faites, total });
  }

  if (!perimetre.includes('oeuvres')) return { faites, total };
  const identifiants = new Set<string>();
  for (const resultat of resultats) {
    for (const oeuvre of (resultat as { oeuvres?: readonly { id: string }[] }).oeuvres ?? []) {
      if (identifiants.size < FICHES_MAX) identifiants.add(oeuvre.id);
    }
  }
  const file = [...identifiants];
  total += file.length;
  surProgression({ faites, total });
  const travailleur = async (): Promise<void> => {
    for (let id = file.shift(); id !== undefined; id = file.shift()) {
      exigerActuelle(portee);
      const courant = id;
      await client.fetchQuery({
        queryKey: cleFicheOeuvre(slug, courant),
        queryFn: (contexte) => oeuvresApi.obtenir(slug, courant, appelDeRequete(contexte)),
      });
      faites += 1;
      surProgression({ faites, total });
    }
  };
  await Promise.all(Array.from({ length: PARALLELE }, travailleur));
  return { faites, total };
}
